/**
 * Settings screen.
 *
 * Kate put three things behind Settings: the attack key ("you can choose a random
 * button like in settings"), the touch control layout ("In settings, can you change
 * like how you use your controller on your iPad? Yes, you can"), and difficulty
 * ("you can choose your difficulty").
 *
 * It is also the anti-cheat boundary she drew: "You can only go into settings, and you
 * can't go any farther than that, and cheat and make yourself into something without
 * buying it." So nothing that grants morphs, coupons or items may ever appear here.
 */
import { keyLabel, type Settings, type SettingsStore } from '../input/settings'

export class SettingsScreen {
  private readonly root: HTMLDivElement
  private readonly store: SettingsStore
  private listeningForKey = false
  /** Notified when the screen opens or closes, so gameplay input can be suspended. */
  onOpenChange: ((open: boolean) => void) | null = null
  private onKeyCapture: ((e: KeyboardEvent) => void) | null = null

  constructor(store: SettingsStore) {
    this.store = store
    this.root = document.createElement('div')
    this.root.className = 'settings'
    this.root.hidden = true
    document.body.append(this.root)
    this.render(store.current)
    store.subscribe((s) => this.render(s))
  }

  get isOpen(): boolean {
    return !this.root.hidden
  }

  open(): void {
    this.root.hidden = false
    this.onOpenChange?.(true)
  }

  close(): void {
    this.stopListening()
    this.root.hidden = true
    this.onOpenChange?.(false)
  }

  toggle(): void {
    if (this.isOpen) this.close()
    else this.open()
  }

  private render(s: Settings): void {
    this.root.innerHTML = ''

    const panel = document.createElement('div')
    panel.className = 'settings-panel'

    const title = document.createElement('h2')
    title.textContent = 'Settings'
    panel.append(title)

    const attackButton = document.createElement('button')
    attackButton.className = 'settings-key'
    attackButton.textContent = this.listeningForKey ? 'press any key…' : keyLabel(s.attackKey)
    attackButton.addEventListener('click', () => this.startListening())
    panel.append(
      this.row('Attack', attackButton,
        'Bite or attack. Hold it on another animal for 5 seconds to kill it.'),
    )

    panel.append(
      this.row('D-pad side',
        this.choice(['left', 'right'], s.touchHandedness, (v) =>
          this.store.set('touchHandedness', v as Settings['touchHandedness'])),
        'Which side of the screen the D-pad sits on, on iPad and phone.'),
    )

    panel.append(
      this.row('Button size',
        this.slider(0.7, 1.5, 0.05, s.touchScale, (v) => this.store.set('touchScale', v)),
        'How big the on-screen controls are.'),
    )

    panel.append(
      this.row('Look speed',
        this.slider(0.3, 2.5, 0.05, s.lookSensitivity, (v) => this.store.set('lookSensitivity', v)),
        'How fast looking around moves the camera.'),
    )

    panel.append(
      this.row('Difficulty',
        this.choice(['easy', 'normal', 'hard'], s.difficulty, (v) =>
          this.store.set('difficulty', v as Settings['difficulty'])),
        'Offline only. The harder it is, the harder the AI animals are.'),
    )

    const actions = document.createElement('div')
    actions.className = 'settings-actions'

    const reset = document.createElement('button')
    reset.textContent = 'Reset to defaults'
    reset.addEventListener('click', () => this.store.reset())

    const done = document.createElement('button')
    done.className = 'settings-done'
    done.textContent = 'Done'
    done.addEventListener('click', () => this.close())

    actions.append(reset, done)
    panel.append(actions)
    this.root.append(panel)
  }

  private row(label: string, control: HTMLElement, hint: string): HTMLElement {
    const row = document.createElement('div')
    row.className = 'settings-row'
    const name = document.createElement('label')
    name.textContent = label
    const note = document.createElement('p')
    note.className = 'settings-hint'
    note.textContent = hint
    row.append(name, control, note)
    return row
  }

  private slider(min: number, max: number, step: number, value: number,
                 onChange: (v: number) => void): HTMLElement {
    const input = document.createElement('input')
    input.type = 'range'
    input.min = String(min)
    input.max = String(max)
    input.step = String(step)
    input.value = String(value)
    input.addEventListener('input', () => onChange(Number(input.value)))
    return input
  }

  private choice(options: string[], selected: string, onPick: (value: string) => void): HTMLElement {
    const group = document.createElement('div')
    group.className = 'settings-choice'
    for (const option of options) {
      const button = document.createElement('button')
      button.textContent = option
      button.className = option === selected ? 'is-selected' : ''
      button.addEventListener('click', () => onPick(option))
      group.append(button)
    }
    return group
  }

  private startListening(): void {
    if (this.listeningForKey) return
    this.listeningForKey = true
    this.render(this.store.current)

    this.onKeyCapture = (event: KeyboardEvent): void => {
      event.preventDefault()
      // stopImmediatePropagation, not stopPropagation: the gameplay key listener is on
      // window too, and listeners on the same node still run under stopPropagation.
      event.stopImmediatePropagation()
      if (event.code !== 'Escape') this.store.set('attackKey', event.code)
      this.stopListening()
      this.render(this.store.current)
    }
    // Capture phase so the game's own key handling never sees the rebinding press.
    window.addEventListener('keydown', this.onKeyCapture, { capture: true, once: true })
  }

  private stopListening(): void {
    if (this.onKeyCapture) {
      window.removeEventListener('keydown', this.onKeyCapture, { capture: true })
      this.onKeyCapture = null
    }
    this.listeningForKey = false
  }
}
